/* ==========================================================================
   Gallery dots.

   Dawn tells you where you are in the gallery with the text "1 / 5". This
   replaces that with a row of dots, which is what a phone carousel is expected
   to look like and what the reference design uses.

   It deliberately does NOT reach into Dawn's SliderComponent. Dawn has dot
   support, but only inside SlideshowComponent, which the product gallery is
   not — wiring to it would mean depending on internals that a theme update can
   move. Everything here is read off the scroll container instead: position is
   scrollLeft / clientWidth, and moving is scrollTo. Those are DOM, not Dawn.

   With this file blocked the gallery still works exactly as before: it still
   swipes, the arrows still step, and CSS keeps the "1 / 5" counter hidden only
   when the dots have actually been built (the .cs-has-dots class below).
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(gallery) {
    if (gallery.dataset.csDotsReady) return;

    var slider = gallery.querySelector('media-gallery > slider-component') || gallery.querySelector('slider-component');
    if (!slider) return;

    var list = slider.querySelector('.product__media-list');
    if (!list) return;

    /* Only the slides that are actually in play. With hide_variants on, the
       Liquid has already dropped the per-variant duplicates, but a hidden
       slide would still be counted here and give a dot that leads nowhere. */
    var slides = Array.prototype.filter.call(
      list.querySelectorAll('.product__media-item'),
      function (li) { return li.offsetWidth > 0 || li.getClientRects().length > 0; }
    );
    if (slides.length < 2) return;

    gallery.dataset.csDotsReady = '1';

    var dots = document.createElement('div');
    dots.className = 'cs-gdots';
    dots.setAttribute('role', 'group');
    dots.setAttribute('aria-label', 'Choose a product image');

    var buttons = slides.map(function (slide, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cs-gdots__dot';
      b.setAttribute('aria-label', 'Image ' + (i + 1) + ' of ' + slides.length);
      b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      b.addEventListener('click', function () {
        var w = list.clientWidth;
        if (!w) return;
        list.scrollTo({ left: i * w, behavior: reduced ? 'auto' : 'smooth' });
      });
      dots.appendChild(b);
      return b;
    });

    slider.insertAdjacentElement('afterend', dots);
    /* Only now is it safe for CSS to hide Dawn's counter — up to this line the
       counter is the only thing telling the reader where they are. */
    gallery.classList.add('cs-has-dots');

    var current = -1;
    var ticking = false;

    /* Five marks, always — a window that slides with you rather than one dot
       per image. Eight images would otherwise put eight marks on screen and the
       indicator stops being an indicator once you have to count it.

       The window is centred on the active page and clamped at both ends, so at
       the start you get the active mark plus the four after it, in the middle
       you get two either side, and at the end the four before it. Each visible
       mark is then sized by its distance from the active one. */
    var WINDOW = 5;

    function mark(i) {
      if (i === current) return;
      current = i;

      var n = buttons.length;
      var start = 0;
      var last = n - 1;
      if (n > WINDOW) {
        start = i - 2;
        if (start < 0) start = 0;
        if (start > n - WINDOW) start = n - WINDOW;
        last = start + WINDOW - 1;
      }

      for (var k = 0; k < n; k++) {
        buttons[k].hidden = k < start || k > last;
        var d = k - i;
        if (d < 0) d = -d;
        if (d > 4) d = 4;
        buttons[k].dataset.csDist = d;
        buttons[k].setAttribute('aria-current', k === i ? 'true' : 'false');
      }
    }

    /* Called here and not above the declarations: `current` is a var, so
       calling mark() before that line runs would set it and then have the
       declaration immediately reset it to -1. */
    mark(0);

    function paint() {
      ticking = false;
      var w = list.clientWidth;
      if (!w) return;
      var i = Math.round(list.scrollLeft / w);
      if (i < 0) i = 0;
      if (i > buttons.length - 1) i = buttons.length - 1;
      mark(i);
    }

    list.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(paint);
      },
      { passive: true }
    );
    window.addEventListener('resize', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(paint);
    }, { passive: true });
  }

  function boot() {
    document.querySelectorAll('.cs-seamless media-gallery').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* The theme editor re-renders the whole section on every setting change, and
     a variant change re-renders the gallery. Both land here. */
  document.addEventListener('shopify:section:load', boot);
})();
